import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Pagination,
  TextField,
  InputAdornment,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddEditFaqModal from 'src/sections/dashboard-sections/AddEditFaqModal';
import ViewFaqModal from 'src/sections/dashboard-sections/ViewFaqModal';
import { getFaqs, getFaqsCategory, deleteFaqs } from 'src/ApiServices';

export default function Faqs() {
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Pagination states
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getFaqsCategory();
        setCategories(response?.data && Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error('Failed to load categories');
      }
    };
    fetchCategories();
  }, []);

  // Fetch FAQs function
  const fetchFaqs = async (currentPage = 1, search = '', categoryId = '') => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (categoryId) {
        params.faq_category_id = categoryId;
      }

      const response = await getFaqs(params);
      setFaqs(response?.data && Array.isArray(response.data) ? response.data : []);

      if (response.pagination) {
        setPagination(response.pagination);
      } else {
        setPagination({
          current_page: currentPage,
          per_page: 20,
          total: response.data?.length || 0,
          last_page: Math.ceil((response.data?.length || 0) / 20) || 1,
          has_more_pages: false
        });
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setFaqs([]);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  // Search handler
  const handleSearchSubmit = useCallback(async () => {
    setPage(1);
    await fetchFaqs(1, searchQuery, selectedCategory);
  }, [searchQuery, selectedCategory]);

  // Clear search handler
  const handleClearSearch = useCallback(async () => {
    setSearchQuery('');
    setPage(1);
    await fetchFaqs(1, '', selectedCategory);
  }, [selectedCategory]);

  // Category change handler
  const handleCategoryChange = useCallback(
    async (event) => {
      const newCategory = event.target.value;
      setSelectedCategory(newCategory);
      setPage(1);
      await fetchFaqs(1, searchQuery, newCategory);
    },
    [searchQuery]
  );

  // Clear category filter
  const handleClearCategory = useCallback(async () => {
    setSelectedCategory('');
    setPage(1);
    await fetchFaqs(1, searchQuery, '');
  }, [searchQuery]);

  // Page change handler
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Load FAQs when page changes
  useEffect(() => {
    fetchFaqs(page, searchQuery, selectedCategory);
  }, [page]);

  // Load initial data
  useEffect(() => {
    fetchFaqs(1, '', '');
  }, []);

  const handleAddClick = () => {
    setEditingFaq(null);
    setModalOpen(true);
  };

  const handleEditClick = (faq) => {
    setEditingFaq(faq);
    setModalOpen(true);
  };

  const handleViewClick = (faq) => {
    setSelectedFaq(faq);
    setViewModalOpen(true);
  };

  const handleDeleteClick = (faq) => {
    setSelectedFaq(faq);
    setDeleteModalOpen(true);
  };

  const handleModalSubmit = async (formData) => {
    try {
      await fetchFaqs(page, searchQuery, selectedCategory);
      toast.success(editingFaq ? 'FAQ updated successfully!' : 'FAQ added successfully!');
      setModalOpen(false);
      setEditingFaq(null);
    } catch (error) {
      console.error('Error in modal submit:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedFaq) return;

    try {
      setDeleteLoading(true);
      await deleteFaqs(selectedFaq.id);
      toast.success('FAQ deleted successfully!');

      // Check if we need to go to previous page after deletion
      if (faqs.length === 1 && page > 1) {
        const newPage = page - 1;
        setPage(newPage);
        await fetchFaqs(newPage, searchQuery, selectedCategory);
      } else {
        await fetchFaqs(page, searchQuery, selectedCategory);
      }

      setDeleteModalOpen(false);
      setSelectedFaq(null);
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      toast.error(error.response?.data?.message || 'Failed to delete FAQ!');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedFaq(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingFaq(null);
  };

  const tableHeaders = ['Category', 'Question', 'Short Answer', 'Created Date', 'Actions'];

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={120} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={200} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={250} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={120} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2, textAlign: 'center' }}>
          <Skeleton
            variant="circular"
            width={40}
            height={40}
            sx={{ display: 'inline-block', mx: 0.5 }}
          />
          <Skeleton
            variant="circular"
            width={40}
            height={40}
            sx={{ display: 'inline-block', mx: 0.5 }}
          />
          <Skeleton
            variant="circular"
            width={40}
            height={40}
            sx={{ display: 'inline-block', mx: 0.5 }}
          />
        </TableCell>
      </TableRow>
    ));

  const renderNoRecordsFound = () => {
    return (
      <TableRow>
        <TableCell
          colSpan={tableHeaders.length}
          sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="h6"
              sx={{ color: palette.text.secondary, fontFamily: 'poppinsMedium', mb: 1 }}
            >
              {searchQuery || selectedCategory
                ? 'No FAQs found matching your criteria'
                : 'No FAQs Found'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: palette.grey[600],
                fontFamily: 'poppinsRegular',
                maxWidth: 400,
                textAlign: 'center'
              }}
            >
              {searchQuery || selectedCategory
                ? 'Try adjusting your search or filter criteria'
                : 'No FAQs available. Add a new FAQ to get started.'}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <Page title={`FAQs | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            mb: 3,
            mt: 2,
            pb: 2,
            pl: 1,
            gap: 1
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Button
            variant="contained"
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              width: { xs: '100%', md: 'auto' },
              '&:hover': { background: palette.primary.dark }
            }}
            onClick={handleAddClick}
            startIcon={<AddIcon />}
          >
            Add FAQ
          </Button>
        </Box>

        {/* Search and Filter Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={9}>
            <TextField
              variant="outlined"
              placeholder="Search FAQs..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: palette.divider },
                  '&:hover fieldset': { borderColor: palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: palette.primary.main }
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel id="category-filter-label">Filter by Category</InputLabel>
              <Select
                labelId="category-filter-label"
                value={selectedCategory}
                onChange={handleCategoryChange}
                label="Filter by Category"
                endAdornment={
                  selectedCategory && (
                    <InputAdornment position="end" sx={{ mr: 2 }}>
                      <IconButton size="small" onClick={handleClearCategory} edge="end">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
                sx={{
                  backgroundColor: palette.background.paper,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.divider },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary.main },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.primary.main
                  }
                }}
              >
                <MenuItem value="">
                  <em>All Categories</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1}>
            <Button
              variant="contained"
              onClick={handleSearchSubmit}
              fullWidth
              sx={{
                height: 56,
                fontSize: '14px',
                fontFamily: 'poppinsMedium'
              }}
              startIcon={<SearchIcon />}
            >
              {/* Search */}
            </Button>
          </Grid>
        </Grid>

        {/* FAQ Table */}
        <Card>
          <TableContainer>
            <Table
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: palette.background.paper,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsSemiBold',
                  fontSize: '14px',
                  borderBottom: `2px solid ${palette.divider}`,
                  py: 2
                },
                '& .MuiTableCell-body': {
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  fontSize: '14px',
                  borderBottom: `1px solid ${palette.divider}`,
                  py: 2
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        whiteSpace: 'nowrap',
                        px: 2,
                        ...(header === 'Actions' && { width: 150, textAlign: 'center' })
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? renderSkeletonRows()
                  : faqs.length > 0
                  ? faqs.map((faq) => (
                      <TableRow
                        key={faq.id}
                        sx={{
                          '&:hover': { backgroundColor: palette.action.hover },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ px: 2 }}>
                          {faq?.faq_category?.name || 'Uncategorized'}
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>{faq.title}</TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Tooltip title={faq.description || 'No description'}>
                            <span>{truncateText(faq.description, 60)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>{formatDate(faq.created_at)}</TableCell>
                        <TableCell sx={{ px: 2, textAlign: 'center' }}>
                          <Tooltip title="View FAQ">
                            <IconButton
                              size="small"
                              onClick={() => handleViewClick(faq)}
                              sx={{ color: palette.info.main }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit FAQ">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(faq)}
                              sx={{ color: palette.primary.main, mx: 1 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete FAQ">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(faq)}
                              sx={{ color: palette.error.main }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  : renderNoRecordsFound()}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Pagination */}
        {!loading && faqs.length > 0 && pagination.total > 0 && pagination.last_page >= 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Add/Edit Modal */}
        <AddEditFaqModal
          open={modalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          editingFaq={editingFaq}
        />

        {/* View Modal */}
        <ViewFaqModal
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          faq={selectedFaq}
        />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1,
              margin: { xs: 2, sm: 3 },
              maxWidth: '400px',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3, pb: 2 }}>
            <WarningAmberIcon sx={{ fontSize: 60, color: palette.warning.main, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}>
              Are you sure you want to delete <strong>"{selectedFaq?.title}"</strong>?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                minWidth: 100,
                '&:hover': {
                  backgroundColor: palette.error.dark
                }
              }}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
